import React, { Dispatch, EffectCallback, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './CreditProtection.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { ICreditProtection } from '../../providers/AcquisitionProvider/models';
import { InputInitial, InputCheckbox } from 'component-library/index';
import { Row, Col, Title, Section, Icon } from '../../importCL';
import SessionStorage from '../../utils/SessionStorage';
import { IFlowApplicationData } from '../../types/shared/FlowApplicationData';
import { safeParse } from '../../utils/cob-utils';

interface ICreditProtectionProps extends MappedComponentProperties {
    addTopBorder?: boolean;
    addBottomBorder?: boolean;
    addSideMargin?: string;
    hideOnApp?: boolean;
    title?: string;
    topDesc?: string;
    topDescBullets?: ITopDescBullet[] | string[];
    topDescBulletIcon?: IIcon;
    firstDisclosureText?: string;
    firstDisclosureLink?: string;
    firstDisclosureIcon?: IIcon;
    secondDisclosureText?: string;
    secondDisclosureLink?: string;
    secondDisclosureIcon?: IIcon;
    yesText?: string;
    agreeText?: string;
    disagreeText?: string;
    bottomDesc?: string;
    initialsText?: string;
    setIsShowing?: Dispatch<SetStateAction<boolean>>;
    instructionalText?: string;
    feeNoticeText?: string;
}

interface ITopDescBullet {
    value: string
}

interface IIcon {
    iconPath: string;
    iconAltText: string;
}

export const CreditProtectionEditConfig = {
    emptyLabel: 'Credit Protection',
    isEmpty: (props: PropsWithChildren<ICreditProtectionProps>) => {
        return !props || !props.firstDisclosureIcon || !props.secondDisclosureIcon;
    },
};

export const checkOfferForCreditProtectionEligibility = async (): Promise<boolean> => {
    const currentProgramCode = await SessionStorage.tryGet('selectedProgramCode', '');

    try {
        const suppressedProgramCodesRaw = (await fetch('/services/v2/contentFragmentServlet.path=/content/dam/cob-corp-acquisition/content-fragments/credit-protection-suppress/credit-protection-suppress'))
        const suppressedProgramCodes = await suppressedProgramCodesRaw.json() as Array<{
            keyValues0: Array<string>
        }>;
        if (
            suppressedProgramCodes 
            && suppressedProgramCodes.length 
            && suppressedProgramCodes[0]
            && suppressedProgramCodes[0].keyValues0.some(
                    (listEntryRaw: string) => 
                        safeParse(listEntryRaw)?.keyValues0_value === currentProgramCode
                )
        ) {
            return false;
        }
    } catch (_) {}
    return true;

}

export const getApplicantInformationForCreditProtection = async (): Promise<{CreditProtectionSelected: boolean, CreditProtectionInitials: string}> => {
    const toReturn = {
        CreditProtectionSelected: false,
        CreditProtectionInitials: '',
    }

    // PQ / SkipPQ / PQofOne
    if (SessionStorage.verifyNotEmpty('prequalFullAppData')) {
        const pqFullAppData = await SessionStorage.tryGetObjectOrCipher('prequalFullAppData', {});

        if (
            pqFullAppData?.CreditProtectionSelected
            && pqFullAppData?.CreditProtectionInitials
        ) {
            toReturn.CreditProtectionSelected = pqFullAppData.CreditProtectionSelected;
            toReturn.CreditProtectionInitials = pqFullAppData.CreditProtectionInitials;
        }
    }

    // PA
    if (SessionStorage.verifyNotEmpty('preApprovalFullAppData')) {
        const paFullAppData = await SessionStorage.tryGetObjectOrCipher('preApprovalFullAppData', {});

        if (
            paFullAppData?.CreditProtection?.IsCreditProtectionSelected
            && paFullAppData?.CreditProtection?.CreditProtectionInitials
        ) {
            toReturn.CreditProtectionSelected = paFullAppData.CreditProtection.IsCreditProtectionSelected;
            toReturn.CreditProtectionInitials = paFullAppData.CreditProtection.CreditProtectionInitials;
        }
    }

    // PHA
    if (SessionStorage.verifyNotEmpty('flowApplicationData')) {
        const flowAppData = await SessionStorage.tryGetObjectOrCipher<IFlowApplicationData>('flowApplicationData', {});

        if ( // Assuming CreditProtection is eligible for the selected program code
            flowAppData?.creditProtectionSelected
            && flowAppData?.creditProtectionInitials
        ) {
            toReturn.CreditProtectionSelected = flowAppData?.creditProtectionSelected;
            toReturn.CreditProtectionInitials = flowAppData?.creditProtectionInitials;
        }
    }

    return toReturn;
}

const CreditProtection = (props: PropsWithChildren<ICreditProtectionProps>): JSX.Element => {
    const [eligibleToShow, setEligibleToShow] = useState(!props.hideOnApp);

    const [showAgreement, setShowAgreement] = useState(false);
    const [customerInitials, setCustomerInitials] = useState('');
    const [isInitialsValid, setIsInitialsValid] = useState<boolean>(true);
    const { topDesc = '' } = props;

    const { 
        setCreditProtectionInfo, 
        setIsCreditProtectionInfoValid,
        hasFocusedCreditProtection,
        setHasFocusedCreditProtection,
    } = useAcquisitionContext();


    const retrieveDescBullets = (): JSX.Element | null => {
        if (!props.topDescBullets || !props.topDescBulletIcon)
            return;

        const arr: ITopDescBullet[] = [];

        props.topDescBullets.forEach((item: ITopDescBullet | string) => {
            if (typeof item === 'string') {
                arr.push({ 'value': item });
            } else {
                arr.push(item);
            }
        });
        return (
            <>
                {arr && arr.map((bullet: ITopDescBullet, index) => {
                    return (
                        <React.Fragment key={index}>
                            { props.topDescBulletIcon?.iconPath &&
                                    <div className='CreditProtection__bulletIconContainer'>
                                        <Icon className='CreditProtection__bulletIcon'
                                            src={props.topDescBulletIcon.iconPath}
                                            alt={props.topDescBulletIcon.iconAltText}
                                            loading='lazy'
                                            size='utility' />
                                    </div>
                            }
                            <p className='CreditProtection__section--li'>{bullet.value && transformHTMLParserLinks(bullet.value)}</p>
                        </React.Fragment >
                    );
                })
                }
            </>
        );
    };

    const retrievePDFIcons = (icon: string[] | IIcon | undefined) => {
        if (!icon) {
            return;
        }

        if (!Array.isArray(icon)) {
            return (
                <>
                    <Icon loading='lazy' className='CreditProtection__icon--margin' size='utility'
                        src={icon?.iconPath ?? ''} alt={icon?.iconAltText} />
                </>
            );
        }

        return (
            <>
                <Icon loading='lazy' className='CreditProtection__icon--margin' size='utility'
                    src={icon[0] ?? ''} alt={icon[1]} />
            </>
        );
    };



    useEffect((): void => {
        const evaluateOffer = async (): Promise<void> => {
            const isOfferEligible = await checkOfferForCreditProtectionEligibility();
            if (isOfferEligible) {
                setEligibleToShow(true);

                const appData = await getApplicantInformationForCreditProtection();

                const previouslyAgreed = appData.CreditProtectionSelected
                if (previouslyAgreed) {
                    setShowAgreement(true);

                    const applicantInitials = appData.CreditProtectionInitials
                    setCustomerInitials(applicantInitials);

                    const initialsValidity = applicantInitials && applicantInitials.length >= 2 && applicantInitials.length <= 3
                    setIsCreditProtectionInfoValid(initialsValidity);
                    setIsInitialsValid(initialsValidity);
                }

            } else { // Only runs when Credit Protection was suppressed - Remove this comment for DPM-14428
                setEligibleToShow(false);

                setShowAgreement(false);
                setCustomerInitials('');
                setIsCreditProtectionInfoValid(true);
                setIsInitialsValid(true);
            }
        }

        evaluateOffer().catch((error: unknown) => {
            console.log('Problem retrieving Credit Protection eligibility');
            console.log(error);
        });
    }, []);

    useEffect((): void => {
        setCreditProtectionInfo({
            CreditProtectionSelected: showAgreement,
            CreditProtectionInitials: customerInitials,
        } as ICreditProtection);
    }, [showAgreement, customerInitials]);

    useEffect((): EffectCallback => {
        const errorListener = (): void => {
            // In this context, showAgreement is an alias for CreditProtectionSelected
            if (showAgreement && !customerInitials) {
                setIsInitialsValid(false);
                setIsCreditProtectionInfoValid(false);
            }
        };

        window.addEventListener('acquisitionErrorToggle', errorListener);
        window.addEventListener('acquisitionErrorToggle-creditProtectionProgram', errorListener);

        return (): void => {
            window.removeEventListener('acquisitionErrorToggle', errorListener);
            window.removeEventListener('acquisitionErrorToggle-creditProtectionProgram', errorListener);
        };
    }, [showAgreement, customerInitials]);

    return (
        <div id='credit-protection' className={`CreditProtection ${!props.isInEditor && (props.hideOnApp || !eligibleToShow) ? 'CreditProtection--hide' : ''} `} >
            <Section className='CreditProtection__outerSection'>
                <style>
                    {props.addSideMargin && `
                    .CreditProtection__container--mobilePadding {
                        @media only screen and (max-width: 768px){
                            margin-left: ${props.addSideMargin}rem !important;
                            margin-right:  ${props.addSideMargin}rem !important;
                        }
                    }
                    `}
                </style>
                <div
                    className={`CreditProtection__container ${showAgreement ? 'CreditProtection__agreementsBlock' : ''}
                    ${props.addSideMargin ? 'CreditProtection__container--mobilePadding' : ''} 
                    ${props.addTopBorder ? 'CreditProtection__container--topBorder' : ''}
                    ${props.addBottomBorder ? 'CreditProtection__container--botBorder' : ''}`}>
                    <Row className='px-4'>
                        <Col className='CreditProtection__colContainer' md={9}>
                            {props.title && <Title className='CreditProtection__title'
                                tag='h3'>{transformHTMLParserLinks(props.title)}</Title>}
                            <section className='CreditProtection__section--bullets'>
                                <p className='CreditProtection__bulletTitle'>{transformHTMLParserLinks(topDesc)}</p>
                                {retrieveDescBullets()}
                                
                            </section>
                        </Col>
                    </Row>
                    {props.feeNoticeText &&
                        <div className='CreditProtection__feeNoticeText'>{transformHTMLParserLinks(props.feeNoticeText)}</div>
                    }
                    <Row className='CreditProtection__checkboxContainer px-4'>
                        <Col>
                            <InputCheckbox id='chkCPDisclosureAgree' checkboxId='credit-protection-checkbox' label={props.agreeText && transformHTMLParserLinks(props.agreeText)}
                                value={showAgreement}
                                setValue={(): void => setShowAgreement((prev: boolean) => {
                                    if (prev) {
                                        setCustomerInitials('');
                                        setIsCreditProtectionInfoValid(true);
                                        setIsInitialsValid(true);
                                    }
                                    return !prev;})}
                                />
                        </Col>
                    </Row>
                    <div className={`${showAgreement ? '' : 'CreditProtection__section--hide'} px-4`}>
                        <div>
                            <div className='CreditProtection__links'>
                                <div className='CreditProtection__instructionalText'>
                                    {props.instructionalText}
                                </div>
                                <div className='CreditProtection__linkContainer'>
                                    <a className='CreditProtection__link LINK'
                                        href={props.firstDisclosureLink ?? ''}
                                        target='_blank' rel='noreferrer'>
                                        {retrievePDFIcons(props.firstDisclosureIcon)}
                                        <div className='CreditProtection__linkText'>
                                            {props.firstDisclosureText && transformHTMLParserLinks(props.firstDisclosureText)}
                                        </div>
                                    </a>
                                </div>
                                <div className='CreditProtection__linkContainer'>
                                    <a className='CreditProtection__link LINK'
                                        href={props.secondDisclosureLink ?? ''}
                                        target='_blank' rel='noreferrer'>
                                        {retrievePDFIcons(props.secondDisclosureIcon)}
                                        <div className='CreditProtection__linkText'>
                                            {props.secondDisclosureText && transformHTMLParserLinks(props.secondDisclosureText)}
                                        </div>
                                    </a>
                                </div>
                            </div>
                            <div
                                className='CreditProtection__bottomDescription'>{props.bottomDesc && transformHTMLParserLinks(props.bottomDesc)}
                            </div>
                            <div id='creditProtection-field-container' className='CreditProtection__inputContainer'>
                                <div className='CreditProtection__text--bold'>
                                    <InputInitial
                                        className='CreditProtection__initialsInput'
                                        value={customerInitials}
                                        setValue={(value: string): void => setCustomerInitials(value.substring(0, 3))}
                                        error={!isInitialsValid}
                                        valid={[isInitialsValid, setIsInitialsValid]}
                                        minLength={2}
                                        maxLength={3} 
                                        onBlur={(): void => {
                                            const isValid = customerInitials.length >= 2;
                                            setIsInitialsValid(isValid);
                                            setIsCreditProtectionInfoValid(isValid);
                                        }}
                                        onChange={(): void => setIsInitialsValid(true)}
                                        onFocus={(): void => setHasFocusedCreditProtection(true)}
                                        description={props.initialsText && transformHTMLParserLinks(props.initialsText)}
                                        useSuccessIcon
                                        showSuccessIcon={hasFocusedCreditProtection && isInitialsValid}
                                        hideAlertIcon
                                        noErrorsOrHelper
                                        alternate
                                    />
                                </div>
                            </div>
                            <div className='CreditProtection__inputErrorContainer'>
                                {!isInitialsValid &&
                                    <div className='CreditProtection__inputErrorText'>
                                        {customerInitials.length > 0 ? 'A minimum of 2 initials is required' : 'Provide your initials to confirm enrollment'}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </Section>
        </div>
    );
};

export default CreditProtection;